import React, { useState, useEffect } from "react";
import {
    getFirestore, collection, addDoc, query, orderBy, getDocs, serverTimestamp,
    doc, updateDoc, deleteDoc
} from 'firebase/firestore';
import {
    Autocomplete, Box, Button, Chip, CircularProgress, Collapse, Container,
    Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton,
    Paper, Snackbar, Table, TableBody, TableCell, TableContainer, TableHead,
    TablePagination, TableRow, TextField, Tooltip, Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import MuiAlert from '@mui/material/Alert';
import moment from "moment";
import { getCuadrillasBasic } from "../../services/cuadrillasService";

const currentUser = JSON.parse(localStorage.getItem("currentUser"));
const firestore = getFirestore();
const userEmail = (currentUser?.email || currentUser?.user || currentUser?.usuario || '').toLowerCase();
const userName = (currentUser?.userName || '').toLowerCase();
const isSpecialDescuentosUser = [
    'isabel.ortega@cbaconstruir.com.py',
    'fiorelha.sandoval@cbaconstruir.com.py',
    'isabelortega',
    'fiorelhasandoval',
    'isabel.ortega',
    'fiorelha.sandoval'
].some(a => userEmail.includes(a) || userName.includes(a));

const isAdmin = currentUser?.rol === 'admin';
const isWrite = currentUser?.rol === 'write' || isSpecialDescuentosUser;

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₲ 0';
    return new Intl.NumberFormat('es-PY', {
        style: 'currency', currency: 'PYG',
        minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(amount);
};

const TIPOS_DESCUENTO = [
    'GARANTIAS INTERNAS',
    'GARANTIAS EXTERNAS',
    'EXCESO CONSUMO CORPORATIVO',
    'MALA DESCARGA MATERIALES',
    'HERRAMIENTAS',
    'REINSTALACIONES CONSIDERADAS COMO SOPORTE',
    'BONIFICACION',
    'OTRO',
];

const emptyForm = { fecha: null, cuadrilla: null, tipo: null, tipoOtro: '', monto: '', observaciones: '' };
const emptyFilters = { desde: null, hasta: null, cuadrilla: null, tipo: null };

const FormFields = ({ values, onChange, cuadrillas }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                    label="Fecha"
                    inputFormat="DD/MM/YYYY"
                    value={values.fecha}
                    onChange={(v) => onChange('fecha', v)}
                    renderInput={(params) => <TextField {...params} sx={{ flex: 1, minWidth: 180 }} />}
                />
            </LocalizationProvider>
            <Autocomplete
                options={cuadrillas}
                getOptionLabel={(o) => o.usuario ? `${o.usuario} - ${o.numero}` : (o.numero || '')}
                value={values.cuadrilla}
                onChange={(_, v) => onChange('cuadrilla', v)}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                sx={{ flex: 1, minWidth: 220 }}
                renderInput={(params) => <TextField {...params} label="Cuadrilla" />}
            />
        </Box>
        <Autocomplete
            options={TIPOS_DESCUENTO}
            value={values.tipo}
            onChange={(_, v) => onChange('tipo', v)}
            renderInput={(params) => <TextField {...params} label="Tipo (Descuento o Bonificación)" />}
        />
        {values.tipo === 'OTRO' && (
            <TextField
                label="Especificar tipo"
                value={values.tipoOtro}
                onChange={(e) => onChange('tipoOtro', e.target.value)}
                placeholder="Descripción del registro..."
            />
        )}
        <TextField
            label="Monto (₲)"
            type="number"
            value={values.monto}
            onChange={(e) => onChange('monto', e.target.value)}
            inputProps={{ min: 0 }}
        />
        <TextField
            label="Observaciones"
            multiline
            rows={2}
            value={values.observaciones}
            onChange={(e) => onChange('observaciones', e.target.value)}
        />
    </Box>
);

const Descuentos = () => {
    const [form, setForm] = useState(emptyForm);
    const [cuadrillas, setCuadrillas] = useState([]);
    const [registros, setRegistros] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingList, setLoadingList] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, text: '', type: 'success' });
    const [editDialog, setEditDialog] = useState({ open: false, registro: null });
    const [editForm, setEditForm] = useState(emptyForm);
    const [editLoading, setEditLoading] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, registro: null });
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [formOpen, setFormOpen] = useState(true);
    const [filters, setFilters] = useState(emptyFilters);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const loadRegistros = async () => {
        setLoadingList(true);
        try {
            const q = query(collection(firestore, 'descuentos'), orderBy('createdAt', 'desc'));
            const snap = await getDocs(q);
            setRegistros(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingList(false);
        }
    };

    useEffect(() => {
        getCuadrillasBasic(setCuadrillas);
        loadRegistros();
    }, []);

    // --- Filtering ---
    const filteredRegistros = registros.filter(r => {
        if (filters.desde && r.fecha && r.fecha < moment(filters.desde).format("YYYYMMDD")) return false;
        if (filters.hasta && r.fecha && r.fecha > moment(filters.hasta).format("YYYYMMDD")) return false;
        if (filters.cuadrilla && r.idCuadrilla !== filters.cuadrilla.id) return false;
        if (filters.tipo && !String(r.tipo || '').toLowerCase().includes(filters.tipo.toLowerCase())) return false;
        return true;
    });

    const totalFiltrado = filteredRegistros.reduce((acc, r) => acc + (r.monto || 0), 0);
    const paginatedRows = filteredRegistros.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const hasActiveFilters = filters.desde || filters.hasta || filters.cuadrilla || filters.tipo;

    const handleClearFilters = () => { setFilters(emptyFilters); setPage(0); };

    const resolveTipo = (tipo, tipoOtro) =>
        tipo === 'OTRO' && tipoOtro?.trim() ? `OTRO: ${tipoOtro.trim()}` : tipo;

    // --- Submit ---
    const handleSubmit = async () => {
        if (!form.fecha || !form.cuadrilla || !form.tipo || !form.monto) {
            setSnackbar({ open: true, text: 'Fecha, cuadrilla, tipo y monto son obligatorios', type: 'error' });
            return;
        }
        if (form.tipo === 'OTRO' && !form.tipoOtro?.trim()) {
            setSnackbar({ open: true, text: 'Debe especificar el tipo de descuento', type: 'error' });
            return;
        }
        setLoading(true);
        try {
            await addDoc(collection(firestore, 'descuentos'), {
                fecha: moment(form.fecha).format("YYYYMMDD"),
                fechaDisplay: moment(form.fecha).format("DD/MM/YYYY"),
                idCuadrilla: form.cuadrilla.id,
                cuadrilla: form.cuadrilla.numero,
                tipo: resolveTipo(form.tipo, form.tipoOtro),
                monto: parseFloat(form.monto),
                observaciones: form.observaciones || '',
                createdAt: serverTimestamp()
            });
            setSnackbar({ open: true, text: 'Descuento registrado correctamente', type: 'success' });
            setForm(emptyForm);
            loadRegistros();
        } catch (e) {
            setSnackbar({ open: true, text: 'Error al guardar el descuento', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // --- Edit ---
    const openEdit = (r) => {
        const fechaMoment = r.fecha ? moment(r.fecha, "YYYYMMDD") : null;
        const cuadrillaObj = cuadrillas.find(c => c.id === r.idCuadrilla)
            || (r.idCuadrilla ? { id: r.idCuadrilla, numero: r.cuadrilla } : null);
        let tipo = r.tipo || null;
        let tipoOtro = '';
        if (tipo && tipo.startsWith('OTRO: ')) { tipoOtro = tipo.replace('OTRO: ', ''); tipo = 'OTRO'; }
        setEditForm({ fecha: fechaMoment, cuadrilla: cuadrillaObj, tipo, tipoOtro, monto: r.monto ?? '', observaciones: r.observaciones || '' });
        setEditDialog({ open: true, registro: r });
    };
    const closeEdit = () => { setEditDialog({ open: false, registro: null }); setEditForm(emptyForm); };
    const handleEditSave = async () => {
        if (!editForm.fecha || !editForm.cuadrilla || !editForm.tipo || !editForm.monto) {
            setSnackbar({ open: true, text: 'Fecha, cuadrilla, tipo y monto son obligatorios', type: 'error' });
            return;
        }
        if (editForm.tipo === 'OTRO' && !editForm.tipoOtro?.trim()) {
            setSnackbar({ open: true, text: 'Debe especificar el tipo de descuento', type: 'error' });
            return;
        }
        setEditLoading(true);
        try {
            await updateDoc(doc(firestore, 'descuentos', editDialog.registro.id), {
                fecha: moment(editForm.fecha).format("YYYYMMDD"),
                fechaDisplay: moment(editForm.fecha).format("DD/MM/YYYY"),
                idCuadrilla: editForm.cuadrilla.id,
                cuadrilla: editForm.cuadrilla.numero,
                tipo: resolveTipo(editForm.tipo, editForm.tipoOtro),
                monto: parseFloat(editForm.monto),
                observaciones: editForm.observaciones || ''
            });
            setSnackbar({ open: true, text: 'Descuento actualizado correctamente', type: 'success' });
            closeEdit();
            loadRegistros();
        } catch (e) {
            setSnackbar({ open: true, text: 'Error al actualizar el descuento', type: 'error' });
        } finally {
            setEditLoading(false);
        }
    };

    // --- Delete ---
    const handleDeleteConfirm = async () => {
        setDeleteLoading(true);
        try {
            await deleteDoc(doc(firestore, 'descuentos', deleteDialog.registro.id));
            setSnackbar({ open: true, text: 'Descuento eliminado correctamente', type: 'success' });
            setDeleteDialog({ open: false, registro: null });
            loadRegistros();
        } catch (e) {
            setSnackbar({ open: true, text: 'Error al eliminar el descuento', type: 'error' });
        } finally {
            setDeleteLoading(false);
        }
    };

    const isValid = form.fecha && form.cuadrilla && form.tipo && form.monto &&
        (form.tipo !== 'OTRO' || form.tipoOtro?.trim());
    const isEditValid = editForm.fecha && editForm.cuadrilla && editForm.tipo && editForm.monto &&
        (editForm.tipo !== 'OTRO' || editForm.tipoOtro?.trim());


    const totalDescuentosFiltrado = filteredRegistros
        .filter(r => !String(r.tipo || '').toUpperCase().includes('BONIFICACION'))
        .reduce((acc, r) => acc + (r.monto || 0), 0);
    const totalBonificacionesFiltrado = filteredRegistros
        .filter(r => String(r.tipo || '').toUpperCase().includes('BONIFICACION'))
        .reduce((acc, r) => acc + (r.monto || 0), 0);

    return (
        <Container maxWidth="md" sx={{ mt: 2 }}>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
                Descuentos / Bonificaciones
            </Typography>

            {/* --- Nuevo Descuento / Bonificación (colapsable) --- */}
            <Paper elevation={3} sx={{ mb: 3, overflow: 'hidden' }}>
                <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                    onClick={() => setFormOpen(o => !o)}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AddIcon color="primary" />
                        <Typography variant="h6">Nuevo Registro (Descuento / Bonificación)</Typography>
                    </Box>
                    <IconButton size="small">{formOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                </Box>
                <Collapse in={formOpen}>
                    <Divider />
                    <Box sx={{ p: 3 }}>
                        <FormFields
                            values={form}
                            onChange={(field, value) => setForm(p => ({ ...p, [field]: value }))}
                            cuadrillas={cuadrillas}
                        />
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={!isValid || loading}
                            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                            sx={{ alignSelf: 'flex-start', mt: 2 }}
                        >
                            {loading ? 'Guardando...' : 'Registrar'}
                        </Button>
                    </Box>
                </Collapse>
            </Paper>

            {/* --- Historial --- */}
            <Paper elevation={3} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="h6">Historial de Descuentos / Bonificaciones</Typography>
                    {hasActiveFilters && (
                        <Chip label="Limpiar filtros" onDelete={handleClearFilters} color="primary" variant="outlined" size="small" />
                    )}
                </Box>

                {/* Filtros */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
                    <FilterListIcon color="action" />
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                            label="Desde"
                            inputFormat="DD/MM/YYYY"
                            value={filters.desde}
                            onChange={(v) => { setFilters(f => ({ ...f, desde: v })); setPage(0); }}
                            renderInput={(params) => <TextField {...params} size="small" sx={{ width: 160 }} />}
                        />
                        <DatePicker
                            label="Hasta"
                            inputFormat="DD/MM/YYYY"
                            value={filters.hasta}
                            onChange={(v) => { setFilters(f => ({ ...f, hasta: v })); setPage(0); }}
                            renderInput={(params) => <TextField {...params} size="small" sx={{ width: 160 }} />}
                        />
                    </LocalizationProvider>
                    <Autocomplete
                        options={cuadrillas}
                        getOptionLabel={(o) => o.usuario ? `${o.usuario} - ${o.numero}` : (o.numero || '')}
                        value={filters.cuadrilla}
                        onChange={(_, v) => { setFilters(f => ({ ...f, cuadrilla: v })); setPage(0); }}
                        sx={{ width: 220 }}
                        renderInput={(params) => <TextField {...params} label="Cuadrilla" size="small" />}
                    />
                    <Autocomplete
                        options={TIPOS_DESCUENTO}
                        value={filters.tipo}
                        onChange={(_, v) => { setFilters(f => ({ ...f, tipo: v })); setPage(0); }}
                        sx={{ width: 220 }}
                        renderInput={(params) => <TextField {...params} label="Tipo" size="small" />}
                    />
                </Box>

                {loadingList ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell><strong>Fecha</strong></TableCell>
                                        <TableCell><strong>Cuadrilla</strong></TableCell>
                                        <TableCell><strong>Tipo</strong></TableCell>
                                        <TableCell align="right"><strong>Monto</strong></TableCell>
                                        <TableCell><strong>Observaciones</strong></TableCell>
                                        {(isAdmin || isWrite) && <TableCell align="center"><strong>Acciones</strong></TableCell>}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedRows.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={isAdmin || isWrite ? 6 : 5} align="center">
                                                {hasActiveFilters ? 'No hay registros con los filtros aplicados' : 'No hay registros'}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        paginatedRows.map((r) => (
                                            <TableRow key={r.id} hover>
                                                <TableCell>{r.fechaDisplay || r.fecha}</TableCell>
                                                <TableCell>{r.cuadrilla}</TableCell>
                                                <TableCell>{r.tipo}</TableCell>
                                                <TableCell align="right">{formatCurrency(r.monto)}</TableCell>
                                                <TableCell sx={{ maxWidth: 250 }}>
                                                    <Tooltip title={r.observaciones || ''} arrow placement="top">
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                maxWidth: 250,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                                display: 'block'
                                                            }}
                                                        >
                                                            {r.observaciones}
                                                        </Typography>
                                                    </Tooltip>
                                                </TableCell>
                                                {(isAdmin || isWrite) && (
                                                    <TableCell align="center">
                                                        <Tooltip title="Editar">
                                                            <IconButton size="small" onClick={() => openEdit(r)}>
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        {isAdmin && (
                                                            <Tooltip title="Eliminar">
                                                                <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, registro: r })}>
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Total + Paginación */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" color="text.secondary">
                                Descuentos: <strong style={{ color: '#d32f2f' }}>{formatCurrency(totalDescuentosFiltrado)}</strong>
                                {' | '}
                                Bonificaciones: <strong style={{ color: '#2e7d32' }}>{formatCurrency(totalBonificacionesFiltrado)}</strong>
                                {hasActiveFilters && ` — ${filteredRegistros.length} de ${registros.length} registros`}
                            </Typography>
                            <TablePagination
                                component="div"
                                count={filteredRegistros.length}
                                page={page}
                                onPageChange={(_, p) => setPage(p)}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                rowsPerPageOptions={[10, 20, 50]}
                                labelRowsPerPage="Filas:"
                                labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                            />
                        </Box>
                    </>
                )}
            </Paper>

            {/* --- Edit Dialog --- */}
            <Dialog open={editDialog.open} onClose={closeEdit} fullWidth maxWidth="sm">
                <DialogTitle>Editar Descuento</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1 }}>
                        <FormFields
                            values={editForm}
                            onChange={(field, value) => setEditForm(p => ({ ...p, [field]: value }))}
                            cuadrillas={cuadrillas}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeEdit} disabled={editLoading}>Cancelar</Button>
                    <Button
                        variant="contained"
                        onClick={handleEditSave}
                        disabled={!isEditValid || editLoading}
                        startIcon={editLoading ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                        {editLoading ? 'Guardando...' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* --- Delete Dialog --- */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, registro: null })}>
                <DialogTitle>Confirmar Eliminación</DialogTitle>
                <DialogContent>
                    <Typography>
                        ¿Estás seguro de que deseas eliminar el descuento
                        {deleteDialog.registro && ` de ${deleteDialog.registro.cuadrilla} por ${formatCurrency(deleteDialog.registro.monto)}`}?
                    </Typography>
                    <Alert severity="warning" sx={{ mt: 2 }}>Esta acción no se puede deshacer.</Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, registro: null })} disabled={deleteLoading}>Cancelar</Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        disabled={deleteLoading}
                        startIcon={deleteLoading ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                        {deleteLoading ? 'Eliminando...' : 'Eliminar'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(p => ({ ...p, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.type}>{snackbar.text}</Alert>
            </Snackbar>
        </Container>
    );
};

export default Descuentos;
